import React from 'react';

const nombreServicio = (s) => {
  if (!s) return 'Servicio';
  const base = s.menu_vinculado_nombre || s.formato_nombre || 'Servicio';
  return s.jornada_etiqueta ? `${base} · ${s.jornada_etiqueta}` : base;
};

const botonSecundario = 'px-2.5 py-1 text-xs font-medium bg-white border rounded-md disabled:opacity-50';

/**
 * Suplemento hasta el mínimo de personas, por servicio (y por jornada en
 * multievento). Lo calcula el servidor (GET /presupuestos/{id}/reglas-comerciales,
 * bloque `suplemento_minimo`) y se pone solo al guardar un borrador sin enviar.
 *
 * - Líneas: cada una con su servicio, "Automático" o "Manual", y los botones
 *   Regalar / Cobrar y Quitar (quitada a mano no se vuelve a poner sola).
 * - Pendientes (presupuesto enviado o confirmado): botones Añadir y Regalar.
 * - Quitadas a mano: botón Volver a añadir.
 * - Menos del mínimo aceptable en un servicio: aviso.
 *
 * @param {object} props
 * @param {object} props.estado bloque `suplemento_minimo` del estado de reglas
 * @param {string|null} props.enCurso acción en curso (`accion:clave`) o null
 * @param {(accion: string, clave?: string) => void} props.onAccion
 * @param {(n: number) => string} props.formatEuros
 */
export default function SuplementoMinimoAviso({ estado, enCurso, onAccion, formatEuros }) {
  if (!estado) return null;
  const lineas = estado.lineas || [];
  const pendientes = estado.pendientes || [];
  const quitadas = estado.quitadas || [];
  const bajos = estado.por_debajo_aceptable || [];
  const manualSinMarca = estado.minimo_manual_sin_marca || [];
  const regaloAntiguo = estado.regalado && lineas.length === 0 ? estado.regalado : null;
  if (lineas.length === 0 && pendientes.length === 0 && quitadas.length === 0 && !regaloAntiguo) return null;

  const ocupado = !!enCurso;
  const textoBoton = (accion, clave, normal, enMarcha) => (enCurso === `${accion}:${clave || ''}` ? enMarcha : normal);

  return (
    <div className="space-y-2" data-testid="aviso-suplemento-minimo">
      {lineas.length > 0 && (
        <div className="px-3 py-2 rounded-lg border bg-white border-gray-200 text-xs text-gray-700 space-y-1.5" data-testid="suplemento-minimo-aplicado">
          <p className="font-semibold text-gray-800">
            Suplemento hasta {estado.minimo} personas por servicio
          </p>
          {lineas.map((l) => (
            <div key={l.id || l.clave} className="flex flex-wrap items-center gap-2" data-testid={`suplemento-minimo-linea-${l.clave || l.id}`}>
              <span className="font-medium">{nombreServicio(l.servicio)}</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${l.automatica ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                {l.automatica ? 'Automático' : 'Manual'}
              </span>
              <span className={l.regalada ? 'line-through text-gray-400' : ''}>
                {l.unidades} × {formatEuros(l.precio_unitario)} = {formatEuros(l.total)}
              </span>
              {l.regalada && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-green-50 text-green-700 border border-green-200">
                  Regalado{l.motivo_cortesia ? `: ${l.motivo_cortesia}` : ''}
                </span>
              )}
              {l.clave && (
                <span className="ml-auto flex gap-1.5">
                  {l.regalada ? (
                    <button type="button" disabled={ocupado} onClick={() => onAccion('cobrar', l.clave)}
                      className={`${botonSecundario} border-gray-300 text-gray-700 hover:bg-gray-50`}>
                      {textoBoton('cobrar', l.clave, 'Cobrar', 'Guardando...')}
                    </button>
                  ) : (
                    <button type="button" disabled={ocupado} onClick={() => onAccion('regalar', l.clave)}
                      className={`${botonSecundario} border-green-300 text-green-800 hover:bg-green-50`}>
                      {textoBoton('regalar', l.clave, 'Regalar', 'Guardando...')}
                    </button>
                  )}
                  <button type="button" disabled={ocupado} onClick={() => onAccion('quitar', l.clave)}
                    className={`${botonSecundario} border-gray-300 text-gray-700 hover:bg-gray-50`}>
                    {textoBoton('quitar', l.clave, 'Quitar', 'Quitando...')}
                  </button>
                </span>
              )}
            </div>
          ))}
          <p className="text-gray-500">Se ajusta solo si cambian las personas de cada servicio.</p>
        </div>
      )}

      {pendientes.length > 0 && (
        <div className="px-3 py-3 rounded-lg border bg-amber-50 border-amber-300 text-sm text-amber-900 space-y-2" data-testid="suplemento-minimo-pendientes">
          {pendientes.map((s) => (
            <div key={s.clave} className="flex flex-wrap items-center gap-2">
              <p className="flex-1 min-w-[220px]">
                <span className="font-semibold">Falta el suplemento de mínimo {s.minimo} en {nombreServicio(s)}:</span>{' '}
                faltan {s.faltan} personas, {s.faltan} × {formatEuros(s.tarifa)} = {formatEuros(s.importe)} ({s.formato_nombre}).
              </p>
              <button type="button" disabled={ocupado} onClick={() => onAccion('anadir', s.clave)}
                className="px-3 py-1.5 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
                {textoBoton('anadir', s.clave, 'Añadir', 'Añadiendo...')}
              </button>
              <button type="button" disabled={ocupado} onClick={() => onAccion('regalar', s.clave)}
                className="px-3 py-1.5 text-xs font-medium bg-white border border-amber-400 text-amber-800 rounded-lg hover:bg-amber-100 disabled:opacity-50">
                {textoBoton('regalar', s.clave, 'Regalar', 'Guardando...')}
              </button>
            </div>
          ))}
          {!estado.auto_permitido && (
            <p className="text-xs text-amber-800">
              El presupuesto ya se ha enviado o confirmado: el suplemento no se añade solo para no cambiar el precio que tiene el cliente.
            </p>
          )}
          {manualSinMarca.length > 0 && (
            <p className="text-xs text-amber-800">
              Hay un suplemento de mínimo puesto a mano ({manualSinMarca.join(', ')}): revísalo antes de añadir otro para no cobrarlo dos veces.
            </p>
          )}
        </div>
      )}

      {quitadas.length > 0 && (
        <div className="px-3 py-2 rounded-lg border bg-gray-50 border-gray-200 text-xs text-gray-600 space-y-1" data-testid="suplemento-minimo-quitadas">
          {quitadas.map((s) => (
            <div key={s.clave} className="flex flex-wrap items-center gap-2">
              <span className="flex-1 min-w-[220px]">
                Quitado a mano: suplemento de mínimo en {nombreServicio(s)} (faltan {s.faltan} personas, {formatEuros(s.importe)}).
              </span>
              <button type="button" disabled={ocupado} onClick={() => onAccion('volver_a_anadir', s.clave)}
                className={`${botonSecundario} border-gray-300 text-gray-700 hover:bg-white`}>
                {textoBoton('volver_a_anadir', s.clave, 'Volver a añadir', 'Añadiendo...')}
              </button>
            </div>
          ))}
        </div>
      )}

      {regaloAntiguo && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500" data-testid="suplemento-minimo-regalado">
          <span>
            Suplemento hasta {estado.minimo} personas regalado por {regaloAntiguo.usuario || regaloAntiguo.usuario_email || 'el equipo'}
            {regaloAntiguo.faltaban != null ? ` (faltaban ${regaloAntiguo.faltaban} personas, ${formatEuros(regaloAntiguo.importe)})` : ''}.
          </span>
          <button type="button" disabled={ocupado} onClick={() => onAccion('quitar_regalo')}
            className="underline hover:text-gray-700 disabled:opacity-50">
            Deshacer
          </button>
        </div>
      )}

      {bajos.map((s) => (
        <p key={`bajo-${s.clave}`} className="text-xs text-red-700 font-medium">
          Menos de {s.minimo_aceptable} personas en {nombreServicio(s)}: normalmente no se acepta el evento.
        </p>
      ))}
    </div>
  );
}
